import { useState, useEffect, useCallback, Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Alert,
  AlertDescription,
} from '../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../contexts/PlanContext';
import api from '../lib/api';
import { toast } from 'sonner';
import Cropper from 'react-easy-crop';
import { 
  Loader2, 
  ArrowLeft, 
  ArrowRight,
  Save, 
  User, 
  Upload, 
  X, 
  ImageIcon, 
  UserPlus, 
  Link as LinkIcon,
  Info,
  Crown,
  Users,
  Briefcase,
  Building2,
  BarChart3,
  Check,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Crop,
  AlertTriangle,
  Plus,
  Key,
  Mail,
  Eye,
  EyeOff,
  Copy
} from 'lucide-react';
import JobProfileDialog from '../components/JobProfileDialog';

// Error Boundary pour capturer les erreurs de rendu
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('EmployeeFormPage Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <DashboardLayout>
          <div className="max-w-4xl mx-auto p-8">
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-8 text-center">
                <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-red-800 mb-2">
                  Une erreur s'est produite
                </h2>
                <p className="text-red-600 mb-4">
                  Impossible d'afficher le formulaire. L'employé peut avoir des données mal formatées.
                </p>
                <div className="flex gap-4 justify-center">
                  <Button 
                    variant="outline" 
                    onClick={() => window.location.href = '/employees'}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour à la liste
                  </Button>
                  <Button 
                    onClick={() => window.location.reload()}
                  >
                    Réessayer
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </DashboardLayout>
      );
    }

    return this.props.children;
  }
}

// Helper function to create cropped image
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getCroppedImg = async (imageSrc, pixelCrop, rotation = 0) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const maxSize = Math.max(image.width, image.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

  canvas.width = safeArea;
  canvas.height = safeArea;

  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-safeArea / 2, -safeArea / 2);

  ctx.drawImage(
    image,
    safeArea / 2 - image.width * 0.5,
    safeArea / 2 - image.height * 0.5
  );

  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
    Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
  );

  // Return as blob with compression
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        resolve(blob);
      },
      'image/jpeg',
      0.85 // Quality 85% for good balance of quality/size
    );
  });
};

// Helper function to extract error message from API errors
const getErrorMessage = (error, defaultMessage = 'Une erreur est survenue') => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') {
    return detail;
  }
  if (Array.isArray(detail)) {
    // Pydantic validation errors
    return detail.map(e => e.msg || e.message || String(e)).join(', ');
  }
  if (detail && typeof detail === 'object') {
    return detail.msg || detail.message || defaultMessage;
  }
  if (error?.message && typeof error.message === 'string') {
    return error.message;
  }
  return defaultMessage;
};

// Composant Stepper
function Stepper({ steps, currentStep, onStepClick }) {
  return (
    <div className="w-full py-6">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep;
          const isClickable = index <= currentStep;
          
          return (
            <div key={step.id} className="flex items-center flex-1">
              {/* Step circle and label */}
              <div className="flex flex-col items-center">
                <button
                  onClick={() => isClickable && onStepClick(index)}
                  disabled={!isClickable}
                  className={`
                    w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300
                    ${isCompleted 
                      ? 'bg-green-500 text-white shadow-lg shadow-green-500/30' 
                      : isCurrent 
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30 ring-4 ring-blue-100' 
                        : 'bg-slate-100 text-slate-400'
                    }
                    ${isClickable ? 'cursor-pointer hover:scale-105' : 'cursor-not-allowed'}
                  `}
                >
                  {isCompleted ? (
                    <Check className="w-6 h-6" />
                  ) : (
                    <step.icon className="w-5 h-5" />
                  )}
                </button>
                <span className={`
                  mt-2 text-sm font-medium text-center max-w-[100px]
                  ${isCurrent ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-slate-400'}
                `}>
                  {step.title}
                </span>
              </div>
              
              {/* Connector line */}
              {index < steps.length - 1 && (
                <div className="flex-1 mx-4 h-1 rounded-full bg-slate-100 overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-500 ${
                      isCompleted ? 'bg-green-500 w-full' : 'bg-slate-100 w-0'
                    }`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Définition des étapes
const STEPS = [
  { id: 'identity', title: 'Identité', icon: User, description: 'Informations personnelles de base' },
  { id: 'professional', title: 'Professionnel', icon: Briefcase, description: 'Poste et organisation' },
  { id: 'hr', title: 'RH & Pilotage', icon: BarChart3, description: 'Données pour les indicateurs' },
  { id: 'review', title: 'Récapitulatif', icon: Check, description: 'Vérifiez les informations' },
];

function EmployeeFormPageContent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isPro, isFree, loading: planLoading } = usePlan();
  const isEditing = !!id;

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [sites, setSites] = useState([]);
  const [societes, setSocietes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [employeeCount, setEmployeeCount] = useState(0);
  const [jobProfiles, setJobProfiles] = useState([]);
  const [managerSearch, setManagerSearch] = useState('');
  const [siteManagerSearch, setSiteManagerSearch] = useState('');  // Recherche Manager de Site
  
  // Dialog for creating job profile
  const [showCreateJobProfileDialog, setShowCreateJobProfileDialog] = useState(false);
  
  // Option pour créer un compte utilisateur
  const [createUserAccount, setCreateUserAccount] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  
  // Image cropper states
  const [cropperOpen, setCropperOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [originalFileName, setOriginalFileName] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  
  const [createMode, setCreateMode] = useState('new');
  const [selectedUserId, setSelectedUserId] = useState('');
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    photoUrl: '',
    departmentId: '',
    siteId: '',
    societeId: '',
    managerId: '',
    siteManagerId: '',  // Manager de Site (optionnel)
    status: 'Actif',
    hireDate: '',
    userId: '',
    jobProfileId: '',
    // Nouveaux champs RH
    matricule: '',
    sexe: 'Homme',
    situationFamiliale: 'Non renseigné',
    typeContrat: 'CDI',
    dateNaissance: '',
    dateSortie: '',
    categorieProfessionnelle: 'Non renseigné',
    motifSortie: '',
    // Type d'entrée pour le calcul des KPI
    typeEntree: 'Nouvelle recrue',
    // Bio/Parcours
    bio: '',
    // Champs pour la fiche publique
    valeursCles: ['', ''],
    missions: ['', '', '', ''],
    linkedinUrl: '',
    // Champs spécifiques production (ouvriers)
    productionLine: '',
    shift: ''
  });

  // Référentiels production (chargés à la demande)
  const [productionLines, setProductionLines] = useState([]);
  const [shifts, setShifts] = useState([]);

  useEffect(() => {
    fetchData();
    if (isEditing) {
      fetchEmployee();
    }
  }, [id]);

  const fetchData = async () => {
    try {
      // Charger les données de manière non bloquante
      const [deptRes, siteRes, societeRes, empRes, usersRes, countRes, plRes, shiftRes] = await Promise.all([
        api.get('/departments').catch(() => ({ data: { departments: [] } })),
        api.get('/sites').catch(() => ({ data: [] })),
        api.get('/societes').catch(() => ({ data: [] })),
        api.get('/employees/all').catch(() => ({ data: [] })),
        api.get('/users/available-for-linking').catch(() => ({ data: [] })),
        api.get('/employees/count').catch(() => ({ data: { count: 0 } })),
        api.get('/production-lines').catch(() => ({ data: { productionLines: [] } })),
        api.get('/shifts').catch(() => ({ data: { shifts: [] } }))
      ]);
      setDepartments(deptRes.data?.departments || deptRes.data || []);
      setSites(siteRes.data?.sites || siteRes.data || []);
      setSocietes(societeRes.data || []);
      setEmployees(empRes.data?.employees || empRes.data || []);
      setAvailableUsers(usersRes.data || []);
      setEmployeeCount(countRes.data?.count || 0);
      setProductionLines(plRes.data?.productionLines || []);
      setShifts(shiftRes.data?.shifts || []);
      
      // Charger les profils de poste si plan PRO
      if (isPro) {
        try {
          const profilesRes = await api.get('/skills/job-profiles', {
            params: { company_id: user?.companyId }
          });
          setJobProfiles(profilesRes.data?.profiles || []);
        } catch (err) {
          console.error('Error loading job profiles:', err);
          setJobProfiles([]);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchEmployee = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/employees/${id}`);
      const emp = response.data;
      
      // Fonction helper pour parser les dates de manière sûre
      const parseDate = (dateValue) => {
        if (!dateValue) return '';
        try {
          // Si c'est déjà une date ISO avec T
          if (typeof dateValue === 'string' && dateValue.includes('T')) {
            return dateValue.split('T')[0];
          }
          // Si c'est une date simple YYYY-MM-DD
          if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
            return dateValue;
          }
          // Essayer de parser d'autres formats
          const date = new Date(dateValue);
          if (!isNaN(date.getTime())) {
            return date.toISOString().split('T')[0];
          }
          return '';
        } catch {
          return '';
        }
      };
      
      setFormData({
        firstName: emp.firstName || '',
        lastName: emp.lastName || '',
        email: emp.email || '',
        phone: emp.phone || '',
        jobTitle: emp.jobTitle || '',
        photoUrl: emp.photoUrl || '',
        departmentId: emp.departmentId || '',
        siteId: emp.siteId || '',
        societeId: emp.societeId || '',
        managerId: emp.managerId || '',
        siteManagerId: emp.siteManagerId || '',  // Manager de Site
        status: emp.status || 'Actif',
        hireDate: parseDate(emp.hireDate),
        userId: emp.userId || '',
        jobProfileId: emp.jobProfileId || '',
        matricule: emp.matricule || '',
        sexe: emp.sexe || 'Non renseigné',
        situationFamiliale: emp.situationFamiliale || 'Non renseigné',
        typeContrat: emp.typeContrat || 'CDI',
        dateNaissance: parseDate(emp.dateNaissance),
        dateSortie: parseDate(emp.dateSortie),
        categorieProfessionnelle: emp.categorieProfessionnelle || 'Non renseigné',
        motifSortie: emp.motifSortie || '',
        typeEntree: emp.typeEntree || 'Nouvelle recrue',
        bio: emp.bio || '',
        valeursCles: Array.isArray(emp.valeursCles) && emp.valeursCles.length ? emp.valeursCles : ['', ''],
        missions: Array.isArray(emp.missions) && emp.missions.length ? [...emp.missions, '', '', '', ''].slice(0, 4) : ['', '', '', ''],
        linkedinUrl: emp.linkedinUrl || '',
        productionLine: emp.productionLine || '',
        shift: emp.shift || ''
      });
      setOriginalManagerId(emp.managerId || '');
    } catch (error) {
      console.error('Error fetching employee:', error);
      toast.error(getErrorMessage(error, 'Erreur lors du chargement'));
      navigate('/employees');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleArrayChange = (field, index, value) => {
    setFormData(prev => {
      const newArray = [...(prev[field] || [])];
      newArray[index] = value;
      return { ...prev, [field]: newArray };
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Accept larger files since we'll compress them after cropping
    if (file.size > 50 * 1024 * 1024) {
      toast.error('La photo ne doit pas dépasser 50 Mo');
      return;
    }
    
    // Create a URL for the image and open the cropper
    const imageUrl = URL.createObjectURL(file);
    setImageToCrop(imageUrl);
    setOriginalFileName(file.name);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCropperOpen(true);
    
    // Reset the input so the same file can be selected again
    e.target.value = '';
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropCancel = () => {
    setCropperOpen(false);
    setImageToCrop(null);
    setOriginalFileName('');
  };

  const handleCropConfirm = async () => {
    if (!imageToCrop || !croppedAreaPixels) return;
    
    try {
      setUploading(true);
      
      // Get the cropped image as a blob
      const croppedBlob = await getCroppedImg(imageToCrop, croppedAreaPixels, rotation);
      
      // Create a file from the blob
      const fileName = originalFileName.replace(/\.[^/.]+$/, '') + '_cropped.jpg';
      const croppedFile = new File([croppedBlob], fileName, { type: 'image/jpeg' });
      
      // Show the size reduction
      const originalSize = imageToCrop.length; // This is approximate
      const newSize = croppedBlob.size;
      console.log(`Image compressed: ${(newSize / 1024).toFixed(0)} KB`);
      
      // Upload the cropped image
      const formDataUpload = new FormData();
      formDataUpload.append('file', croppedFile);
      
      const response = await api.post('/upload/image', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setFormData(prev => ({ ...prev, photoUrl: response.data.url }));
      toast.success(`Photo recadrée et uploadée (${(newSize / 1024).toFixed(0)} KB)`);
      
      // Close the cropper
      setCropperOpen(false);
      setImageToCrop(null);
      setOriginalFileName('');
    } catch (error) {
      console.error('Crop/Upload error:', error);
      toast.error(getErrorMessage(error, 'Erreur lors de l\'upload'));
    } finally {
      setUploading(false);
    }
  };

  const handleUserSelect = (userId) => {
    setSelectedUserId(userId);
    const selectedUser = availableUsers.find(u => u.id === userId);
    if (selectedUser) {
      setFormData(prev => ({
        ...prev,
        firstName: selectedUser.firstName || '',
        lastName: selectedUser.lastName || '',
        email: selectedUser.email || '',
        userId: userId
      }));
    }
  };

  const validateStep = (step) => {
    switch(step) {
      case 0: // Identité
        if (!formData.firstName.trim()) {
          toast.error('Le prénom est requis');
          return false;
        }
        if (!formData.lastName.trim()) {
          toast.error('Le nom est requis');
          return false;
        }
        // Email est maintenant optionnel
        return true;
      case 1: // Professionnel
        if (!formData.jobTitle.trim()) {
          toast.error('Le poste est requis');
          return false;
        }
        return true;
      case 2: // RH
        return true;
      case 3: // Récapitulatif
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [createdEmployee, setCreatedEmployee] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false); // Guard contre double soumission
  // Pour détecter un changement de manager au submit
  const [originalManagerId, setOriginalManagerId] = useState('');
  // Dialog pour proposer le resync des évaluations après changement de manager
  const [managerSyncDialog, setManagerSyncDialog] = useState({
    open: false,
    count: 0,
    employeeId: null,
    syncing: false,
  });

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Protection contre la double soumission
    if (isSubmitting || saving) {
      console.log('Soumission déjà en cours, ignorée');
      return;
    }
    
    if (!validateStep(0) || !validateStep(1)) return;

    // Si création de compte demandée, vérifier l'email
    if (createUserAccount && !formData.email) {
      toast.error('Un email est requis pour créer un compte utilisateur');
      setCurrentStep(0);
      return;
    }
    
    setIsSubmitting(true);
    setSaving(true);
    
    try {
      // Préparer les données, filtrer les arrays vides
      const dataToSend = {
        ...formData,
        valeursCles: (formData.valeursCles || []).filter(v => v && v.trim()),
        missions: (formData.missions || []).filter(m => m && m.trim())
      };
      
      if (isEditing) {
        const response = await api.put(`/employees/${id}`, dataToSend);
        if (response.status === 200) {
          toast.success('Employé mis à jour avec succès');
          // Détection changement de manager → proposer resync des évaluations en cours
          const managerChanged = (originalManagerId || '') !== (formData.managerId || '');
          if (managerChanged) {
            try {
              const r = await api.get(`/performance-eval/employees/${id}/active-evaluations-count`);
              const activeCount = r.data?.count || 0;
              if (activeCount > 0) {
                setManagerSyncDialog({ open: true, count: activeCount, employeeId: id, syncing: false });
                return; // Ne navigue pas tant que l'utilisateur n'a pas décidé
              }
            } catch (_e) {
              // En cas d'erreur, on ne bloque pas la navigation
            }
          }
          navigate('/employees', { replace: true });
        }
      } else {
        const response = await api.post('/employees', dataToSend);
        const newEmployee = response.data?.employee || response.data;
        setCreatedEmployee(newEmployee);

        // Créer le compte utilisateur si demandé
        if (createUserAccount && newEmployee?.id) {
          await createUserAccountForEmployee(newEmployee.id, formData);
        }

        setSubmitSuccess(true);
        toast.success('Employé créé avec succès !');
        // Ne pas naviguer immédiatement - laisser l'utilisateur voir le résumé
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error(getErrorMessage(error, 'Erreur lors de la sauvegarde. Veuillez réessayer.'));
      // Ne pas naviguer en cas d'erreur - rester sur le formulaire
    } finally {
      setSaving(false);
      setIsSubmitting(false);
    }
  };

  // Handlers du dialog de resync des évaluations après changement de manager
  const handleSyncEvaluations = async () => {
    if (!managerSyncDialog.employeeId) return;
    setManagerSyncDialog(d => ({ ...d, syncing: true }));
    try {
      const r = await api.post(`/performance-eval/employees/${managerSyncDialog.employeeId}/sync-evaluations-manager`);
      toast.success(r.data?.message || 'Évaluations mises à jour');
      setManagerSyncDialog({ open: false, count: 0, employeeId: null, syncing: false });
      navigate('/employees', { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la synchronisation');
      setManagerSyncDialog(d => ({ ...d, syncing: false }));
    }
  };

  const handleSkipSync = () => {
    setManagerSyncDialog({ open: false, count: 0, employeeId: null, syncing: false });
    navigate('/employees', { replace: true });
  };

  // Fonction pour obtenir le nom du département
  const getDepartmentName = (id) => {
    const dept = departments.find(d => d.id === id);
    return dept?.name || 'Non défini';
  };

  // Callback quand une fiche de poste est créée depuis le dialog
  const handleJobProfileCreated = (savedProfile) => {
    if (savedProfile?.id) {
      // Ajouter le nouveau profil à la liste et le sélectionner
      setJobProfiles(prev => [...prev, savedProfile]);
      handleSelectChange('jobProfileId', savedProfile.id);
      toast.success('Fiche de poste créée et attribuée !');
    }
  };

  // Générer un mot de passe aléatoire
  const generatePassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  // Créer un compte utilisateur pour l'employé
  const createUserAccountForEmployee = async (employeeId, employeeData) => {
    // Au moins un identifiant requis : email, téléphone OU matricule
    if (!employeeData.email && !employeeData.phone && !employeeData.matricule) {
      toast.error('Au moins un identifiant requis (email, téléphone ou matricule) pour créer un compte');
      return null;
    }

    const tempPassword = generatePassword();
    setGeneratedPassword(tempPassword);

    try {
      const response = await api.post('/users/create-for-employee', {
        employeeId,
        email: employeeData.email || null,
        phone: employeeData.phone || null,
        matricule: employeeData.matricule || null,
        firstName: employeeData.firstName,
        lastName: employeeData.lastName,
        password: tempPassword,
        role: 'Employé'
      });
      
      toast.success('Compte utilisateur créé !');
      return response.data;
    } catch (error) {
      console.error('Erreur création compte:', error);
      const detail = error.response?.data?.detail || '';
      if (detail.includes('existe déjà')) {
        toast.error(detail);
      } else {
        toast.error(detail || 'Erreur lors de la création du compte');
      }
      return null;
    }
  };

  // Fonction pour obtenir le nom du site
  const getSiteName = (id) => {
    const site = sites.find(s => s.id === id);
    return site?.name || 'Non défini';
  };

  // Fonction pour obtenir le nom du manager
  const getManagerName = (id) => {
    const manager = employees.find(e => e.id === id);
    return manager ? `${manager.firstName} ${manager.lastName}` : 'Aucun';
  };

  // Filtrer et limiter les employés pour le sélecteur de manager (optimisation performance)
  const getFilteredManagers = () => {
    const filtered = employees
      .filter(e => e.id !== id) // Exclure l'employé courant
      .filter(e => {
        if (!managerSearch.trim()) return true;
        const fullName = `${e.firstName} ${e.lastName}`.toLowerCase();
        return fullName.includes(managerSearch.toLowerCase());
      });
    
    // Si pas de recherche, limiter à 50 premiers + le manager actuel
    if (!managerSearch.trim() && filtered.length > 50) {
      const currentManager = formData.managerId 
        ? employees.find(e => e.id === formData.managerId)
        : null;
      const limited = filtered.slice(0, 50);
      // S'assurer que le manager actuel est inclus
      if (currentManager && !limited.find(e => e.id === currentManager.id)) {
        limited.unshift(currentManager);
      }
      return { list: limited, hasMore: true, total: filtered.length };
    }
    
    return { list: filtered.slice(0, 100), hasMore: filtered.length > 100, total: filtered.length };
  };

  // Filtrer et limiter les employés pour le sélecteur de Manager de Site
  const getFilteredSiteManagers = () => {
    const filtered = employees
      .filter(e => e.id !== id) // Exclure l'employé courant
      .filter(e => {
        if (!siteManagerSearch.trim()) return true;
        const fullName = `${e.firstName} ${e.lastName}`.toLowerCase();
        return fullName.includes(siteManagerSearch.toLowerCase());
      });
    
    // Si pas de recherche, limiter à 50 premiers + le manager de site actuel
    if (!siteManagerSearch.trim() && filtered.length > 50) {
      const currentSiteManager = formData.siteManagerId 
        ? employees.find(e => e.id === formData.siteManagerId)
        : null;
      const limited = filtered.slice(0, 50);
      // S'assurer que le manager de site actuel est inclus
      if (currentSiteManager && !limited.find(e => e.id === currentSiteManager.id)) {
        limited.unshift(currentSiteManager);
      }
      return { list: limited, hasMore: true, total: filtered.length };
    }
    
    return { list: filtered.slice(0, 100), hasMore: filtered.length > 100, total: filtered.length };
  };

  // Fonction pour obtenir le nom du Manager de Site
  const getSiteManagerName = (id) => {
    const manager = employees.find(e => e.id === id);
    return manager ? `${manager.firstName} ${manager.lastName}` : 'Aucun';
  };

  if (loading || planLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/employees')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à la liste
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30">
              <UserPlus className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                {isEditing ? 'Modifier l\'employé' : 'Nouvel employé'}
              </h1>
              <p className="text-slate-500">
                {isEditing ? 'Mettez à jour les informations' : 'Créez une fiche employé en quelques étapes'}
              </p>
            </div>
          </div>
        </div>

        {/* Stepper */}
        <Card className="mb-6 border-0 shadow-sm bg-white/80 backdrop-blur">
          <CardContent className="pt-2">
            <Stepper 
              steps={STEPS} 
              currentStep={currentStep} 
              onStepClick={setCurrentStep}
            />
          </CardContent>
        </Card>

        {/* Form Content */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="border-b bg-slate-50/50">
            <div className="flex items-center gap-3">
              {(() => {
                const StepIcon = STEPS[currentStep].icon;
                return <StepIcon className="w-5 h-5 text-blue-600" />;
              })()}
              <div>
                <CardTitle>{STEPS[currentStep].title}</CardTitle>
                <CardDescription>{STEPS[currentStep].description}</CardDescription>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-6">
            <div>
              
              {/* Step 1: Identité */}
              {currentStep === 0 && (
                <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                  {/* Mode de création */}
                  {!isEditing && availableUsers.length > 0 && (
                    <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                      <Label className="text-blue-900 font-medium mb-3 block">Mode de création</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => { setCreateMode('new'); setSelectedUserId(''); }}
                          className={`p-4 rounded-lg border-2 transition-all ${
                            createMode === 'new' 
                              ? 'border-blue-500 bg-white shadow-md' 
                              : 'border-slate-200 bg-white hover:border-slate-300'
                          }`}
                        >
                          <UserPlus className={`w-6 h-6 mx-auto mb-2 ${createMode === 'new' ? 'text-blue-600' : 'text-slate-400'}`} />
                          <span className={`text-sm font-medium ${createMode === 'new' ? 'text-blue-900' : 'text-slate-600'}`}>
                            Nouvelle fiche
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setCreateMode('link')}
                          className={`p-4 rounded-lg border-2 transition-all ${
                            createMode === 'link' 
                              ? 'border-blue-500 bg-white shadow-md' 
                              : 'border-slate-200 bg-white hover:border-slate-300'
                          }`}
                        >
                          <LinkIcon className={`w-6 h-6 mx-auto mb-2 ${createMode === 'link' ? 'text-blue-600' : 'text-slate-400'}`} />
                          <span className={`text-sm font-medium ${createMode === 'link' ? 'text-blue-900' : 'text-slate-600'}`}>
                            Lier à un compte
                          </span>
                        </button>
                      </div>
                      
                      {createMode === 'link' && (
                        <div className="mt-4">
                          <Select value={selectedUserId} onValueChange={handleUserSelect}>
                            <SelectTrigger className="bg-white">
                              <SelectValue placeholder="Sélectionner un utilisateur..." />
                            </SelectTrigger>
                            <SelectContent>
                              {availableUsers.map(u => (
                                <SelectItem key={u.id} value={u.id}>
                                  {u.firstName} {u.lastName} - {u.email}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Photo de profil */}
                  <div className="flex items-center gap-6 p-4 bg-slate-50 rounded-xl">
                    <div className="relative">
                      {formData.photoUrl ? (
                        <img 
                          src={formData.photoUrl} 
                          alt="Photo" 
                          className="w-24 h-24 rounded-2xl object-cover shadow-lg"
                        />
                      ) : (
                        <div className="w-24 h-24 bg-gradient-to-br from-slate-200 to-slate-300 rounded-2xl flex items-center justify-center">
                          <User className="w-10 h-10 text-slate-400" />
                        </div>
                      )}
                      {formData.photoUrl && (
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, photoUrl: '' }))}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white shadow-lg hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900 mb-1">Photo de profil</h4>
                      <p className="text-sm text-slate-500 mb-3">
                        Vous pourrez recadrer et compresser l'image après sélection.
                      </p>
                      <label className="cursor-pointer">
                        <input 
                          type="file" 
                          accept="image/*" 
                          className="hidden" 
                          onChange={handlePhotoUpload}
                          disabled={uploading}
                        />
                        <span className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crop className="w-4 h-4" />}
                          {uploading ? 'Traitement...' : 'Choisir et recadrer'}
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Informations personnelles */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">Prénom <span className="text-red-500">*</span></Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="Ex: Amadou"
                        className="h-11"
                        disabled={createMode === 'link' && selectedUserId}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Nom <span className="text-red-500">*</span></Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder="Ex: Diallo"
                        className="h-11"
                        disabled={createMode === 'link' && selectedUserId}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email <span className="text-slate-400 text-xs">(optionnel)</span></Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="amadou.diallo@example.com"
                        className="h-11"
                        disabled={createMode === 'link' && selectedUserId}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Téléphone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+223 70 00 00 00"
                        className="h-11"
                      />
                    </div>
                  </div>
                  
                  {/* Bio / Parcours */}
                  <div className="space-y-2">
                    <Label htmlFor="bio">Parcours / À propos</Label>
                    <textarea
                      id="bio"
                      name="bio"
                      value={formData.bio}
                      onChange={handleChange}
                      placeholder="Une courte description du parcours ou des compétences clés de l'employé..."
                      className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-webtech-blue focus:border-transparent resize-none"
                      maxLength={300}
                    />
                    <p className="text-xs text-slate-500">
                      {formData.bio?.length || 0}/300 caractères - Cette description sera affichée sur la fiche collaborateur
                    </p>
                  </div>
                  
                  {/* Valeurs Clés */}
                  <div className="space-y-2">
                    <Label>Valeurs clés (2 max)</Label>
                    <p className="text-xs text-slate-500 mb-2">Mots ou phrases qui décrivent le collaborateur</p>
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        value={formData.valeursCles?.[0] || ''}
                        onChange={(e) => handleArrayChange('valeursCles', 0, e.target.value)}
                        placeholder="Ex: Innovation"
                        className="h-10"
                        maxLength={50}
                      />
                      <Input
                        value={formData.valeursCles?.[1] || ''}
                        onChange={(e) => handleArrayChange('valeursCles', 1, e.target.value)}
                        placeholder="Ex: Leadership"
                        className="h-10"
                        maxLength={50}
                      />
                    </div>
                  </div>
                  
                  {/* Missions */}
                  <div className="space-y-2">
                    <Label>Missions au quotidien (4 max)</Label>
                    <p className="text-xs text-slate-500 mb-2">Résumez les principales responsabilités</p>
                    <div className="space-y-2">
                      {[0, 1, 2, 3].map((index) => (
                        <Input
                          key={index}
                          value={formData.missions?.[index] || ''}
                          onChange={(e) => handleArrayChange('missions', index, e.target.value)}
                          placeholder={`Mission ${index + 1}`}
                          className="h-10"
                          maxLength={150}
                        />
                      ))}
                    </div>
                  </div>
                  
                  {/* LinkedIn */}
                  <div className="space-y-2">
                    <Label htmlFor="linkedinUrl">Profil LinkedIn</Label>
                    <Input
                      id="linkedinUrl"
                      name="linkedinUrl"
                      value={formData.linkedinUrl}
                      onChange={handleChange}
                      placeholder="https://www.linkedin.com/in/..."
                      className="h-11"
                    />
                  </div>
                </div>
              )}

              {/* Step 2: Professionnel */}
              {currentStep === 1 && (
                <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="jobTitle">Poste / Fonction <span className="text-red-500">*</span></Label>
                      <Input
                        id="jobTitle"
                        name="jobTitle"
                        value={formData.jobTitle}
                        onChange={handleChange}
                        placeholder="Ex: Développeur Senior"
                        className="h-11"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="hireDate">Date d'embauche</Label>
                      <Input
                        id="hireDate"
                        name="hireDate"
                        type="date"
                        value={formData.hireDate}
                        onChange={handleChange}
                        className="h-11"
                      />
                    </div>
                  </div>

                  {/* Type d'entrée - Important pour les KPI */}
                  {!isEditing && (
                    <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                      <div className="flex items-center gap-2 mb-3">
                        <UserPlus className="w-4 h-4 text-green-600" />
                        <Label className="text-green-900 font-medium">Type d'entrée (pour les KPI)</Label>
                      </div>
                      <Select
                        value={formData.typeEntree}
                        onValueChange={(value) => handleSelectChange('typeEntree', value)}
                      >
                        <SelectTrigger className="bg-white h-11">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Nouvelle recrue">🆕 Nouvelle recrue (embauche externe)</SelectItem>
                          <SelectItem value="Transfert entrant">🔄 Transfert entrant (mutation interne)</SelectItem>
                          <SelectItem value="Reprise">📋 Reprise (retour congé, réintégration)</SelectItem>
                          <SelectItem value="Régularisation">📝 Régularisation (existant non déclaré)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-green-700 mt-2">
                        💡 Seules les "Nouvelles recrues" comptent comme entrées dans le calcul du turnover.
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Département</Label>
                      <Select
                        value={formData.departmentId || "none"}
                        onValueChange={(value) => handleSelectChange('departmentId', value === "none" ? "" : value)}
                      >
                        <SelectTrigger className="h-11">
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Aucun</SelectItem>
                          {departments.map(dept => (
                            <SelectItem key={dept.id} value={dept.id}>
                              {dept.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Site / Agence</Label>
                      <Select
                        value={formData.siteId || "none"}
                        onValueChange={(value) => handleSelectChange('siteId', value === "none" ? "" : value)}
                      >
                        <SelectTrigger className="h-11">
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Aucun</SelectItem>
                          {sites.map(site => (
                            <SelectItem key={site.id} value={site.id}>
                              {site.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Société / Filiale</Label>
                    <Select
                      value={formData.societeId || "none"}
                      onValueChange={(value) => handleSelectChange('societeId', value === "none" ? "" : value)}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucune</SelectItem>
                        {societes.map(societe => (
                          <SelectItem key={societe.id} value={societe.id}>
                            {societe.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Manager Principal (N+1)</Label>
                      <Select
                        value={formData.managerId || "none"}
                        onValueChange={(value) => {
                          handleSelectChange('managerId', value === "none" ? "" : value);
                          setManagerSearch(''); // Reset search after selection
                        }}
                      >
                        <SelectTrigger className="h-11">
                          <SelectValue placeholder="Aucun (Top niveau)" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {/* Champ de recherche */}
                          <div className="p-2 border-b sticky top-0 bg-white z-10">
                            <Input
                              placeholder="Rechercher un manager..."
                              value={managerSearch}
                              onChange={(e) => setManagerSearch(e.target.value)}
                              className="h-8"
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            />
                          </div>
                          <SelectItem value="none">Aucun (Top niveau)</SelectItem>
                          {(() => {
                            const { list, hasMore, total } = getFilteredManagers();
                            return (
                              <>
                                {list.map(emp => (
                                  <SelectItem key={emp.id} value={emp.id}>
                                    {emp.firstName} {emp.lastName}
                                  </SelectItem>
                                ))}
                                {hasMore && (
                                  <div className="p-2 text-xs text-slate-500 text-center border-t">
                                    {total > 100 
                                      ? `Affichage limité à 100 résultats. Affinez votre recherche.`
                                      : `${total - list.length} autres disponibles. Tapez pour rechercher.`}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Manager de Site <span className="text-slate-400 text-xs">(optionnel)</span></Label>
                      <Select
                        value={formData.siteManagerId || "none"}
                        onValueChange={(value) => {
                          handleSelectChange('siteManagerId', value === "none" ? "" : value);
                          setSiteManagerSearch(''); // Reset search after selection
                        }}
                      >
                        <SelectTrigger className="h-11">
                          <SelectValue placeholder="Aucun" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {/* Champ de recherche */}
                          <div className="p-2 border-b sticky top-0 bg-white z-10">
                            <Input
                              placeholder="Rechercher..."
                              value={siteManagerSearch}
                              onChange={(e) => setSiteManagerSearch(e.target.value)}
                              className="h-8"
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            />
                          </div>
                          <SelectItem value="none">Aucun</SelectItem>
                          {(() => {
                            const { list, hasMore, total } = getFilteredSiteManagers();
                            return (
                              <>
                                {list.map(emp => (
                                  <SelectItem key={emp.id} value={emp.id}>
                                    {emp.firstName} {emp.lastName}
                                  </SelectItem>
                                ))}
                                {hasMore && (
                                  <div className="p-2 text-xs text-slate-500 text-center border-t">
                                    {total > 100 
                                      ? `Affichage limité à 100 résultats. Affinez votre recherche.`
                                      : `${total - list.length} autres disponibles. Tapez pour rechercher.`}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Statut</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(value) => handleSelectChange('status', value)}
                      >
                        <SelectTrigger className="h-11">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Actif">✅ Actif</SelectItem>
                          <SelectItem value="Sorti">🚪 Sorti</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Date d'embauche</Label>
                      <Input
                        type="date"
                        value={formData.hireDate}
                        onChange={(e) => handleChange({ target: { name: 'hireDate', value: e.target.value } })}
                        className="h-11"
                      />
                    </div>
                  </div>

                  {/* Fiche de poste - Module Compétences */}
                  <div className={`p-4 rounded-xl border ${isPro ? 'bg-purple-50 border-purple-200' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Briefcase className={`w-4 h-4 ${isPro ? 'text-purple-600' : 'text-slate-400'}`} />
                        <Label className={`font-medium ${isPro ? 'text-purple-900' : 'text-slate-700'}`}>
                          Fiche de poste
                        </Label>
                      </div>
                      {!isPro && (
                        <Badge variant="outline" className="text-xs text-slate-500">PRO requis</Badge>
                      )}
                    </div>
                    
                    {isPro ? (
                      jobProfiles.length > 0 ? (
                        <div className="space-y-2">
                          <Select
                            value={formData.jobProfileId || "none"}
                            onValueChange={(value) => handleSelectChange('jobProfileId', value === "none" ? "" : value)}
                          >
                            <SelectTrigger className="bg-white">
                              <SelectValue placeholder="Sélectionner une fiche de poste..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Aucune fiche attribuée</SelectItem>
                              {jobProfiles.map(profile => (
                                <SelectItem key={profile.id} value={profile.id}>
                                  {profile.title || profile.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <div className="flex justify-end">
                            <Button 
                              type="button" 
                              variant="link" 
                              size="sm" 
                              className="text-purple-600 h-auto p-0"
                              onClick={() => setShowCreateJobProfileDialog(true)}
                            >
                              + Créer une nouvelle fiche
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-purple-700 bg-purple-100 p-4 rounded-lg space-y-3">
                          <p className="font-medium">Aucune fiche de poste disponible.</p>
                          <p className="text-xs text-purple-600">
                            Créez une fiche de poste pour définir les compétences requises pour ce poste.
                          </p>
                          <Button 
                            type="button"
                            variant="outline" 
                            size="sm"
                            className="bg-white"
                            onClick={() => setShowCreateJobProfileDialog(true)}
                          >
                            <Briefcase className="w-4 h-4 mr-2" />
                            Créer une fiche de poste
                          </Button>
                        </div>
                      )
                    ) : (
                      <p className="text-sm text-slate-500">
                        Passez au plan PRO pour associer des fiches de poste aux employés et activer le module Skills.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Step 3: RH & Pilotage */}
              {currentStep === 2 && (
                <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                  <Alert className="bg-blue-50 border-blue-200">
                    <Info className="w-4 h-4 text-blue-600" />
                    <AlertDescription className="text-blue-800">
                      Ces informations permettent de calculer les indicateurs RH (turnover, ratio H/F, ancienneté moyenne, etc.)
                    </AlertDescription>
                  </Alert>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="matricule">Matricule</Label>
                      <Input
                        id="matricule"
                        name="matricule"
                        value={formData.matricule}
                        onChange={handleChange}
                        placeholder="EMP001"
                        className="h-11"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Sexe</Label>
                      <Select
                        value={formData.sexe}
                        onValueChange={(value) => handleSelectChange('sexe', value)}
                      >
                        <SelectTrigger className="h-11">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Homme">👨 Homme</SelectItem>
                          <SelectItem value="Femme">👩 Femme</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Situation familiale</Label>
                      <Select
                        value={formData.situationFamiliale}
                        onValueChange={(value) => handleSelectChange('situationFamiliale', value)}
                      >
                        <SelectTrigger className="h-11">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Célibataire">Célibataire</SelectItem>
                          <SelectItem value="Marié(e)">Marié(e)</SelectItem>
                          <SelectItem value="Divorcé(e)">Divorcé(e)</SelectItem>
                          <SelectItem value="Veuf(ve)">Veuf(ve)</SelectItem>
                          <SelectItem value="Autre">Autre</SelectItem>
                          <SelectItem value="Non renseigné">Non renseigné</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Type de contrat</Label>
                      <Select
                        value={formData.typeContrat}
                        onValueChange={(value) => handleSelectChange('typeContrat', value)}
                      >
                        <SelectTrigger className="h-11">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CDI">📋 CDI</SelectItem>
                          <SelectItem value="CDD">📄 CDD</SelectItem>
                          <SelectItem value="Apprentissage">🎓 Apprentissage</SelectItem>
                          <SelectItem value="Intérim">⏱️ Intérim</SelectItem>
                          <SelectItem value="Prestation">🤝 Prestation</SelectItem>
                          <SelectItem value="Stage">📚 Stage</SelectItem>
                          <SelectItem value="Autre">📝 Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Catégorie professionnelle</Label>
                      <Select
                        value={formData.categorieProfessionnelle}
                        onValueChange={(value) => handleSelectChange('categorieProfessionnelle', value)}
                      >
                        <SelectTrigger className="h-11">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Cadre">👔 Cadre</SelectItem>
                          <SelectItem value="Agent de maîtrise">👷 Agent de maîtrise</SelectItem>
                          <SelectItem value="Employé">💼 Employé</SelectItem>
                          <SelectItem value="Ouvrier">🔧 Ouvrier</SelectItem>
                          <SelectItem value="Non renseigné">❓ Non renseigné</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Champs spécifiques ouvriers : Ligne de production + Shift */}
                    {formData.categorieProfessionnelle === 'Ouvrier' && (
                      <>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-1">
                            Ligne de production
                            <span className="text-xs text-slate-400">(ouvriers)</span>
                          </Label>
                          <Select
                            value={formData.productionLine || 'none'}
                            onValueChange={(value) => handleSelectChange('productionLine', value === 'none' ? '' : value)}
                          >
                            <SelectTrigger className="h-11" data-testid="employee-production-line-select">
                              <SelectValue placeholder="Sélectionner une ligne" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">— Aucune —</SelectItem>
                              {productionLines.map(pl => (
                                <SelectItem key={pl.id} value={pl.id}>🏭 {pl.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {productionLines.length === 0 && (
                            <p className="text-xs text-amber-600">
                              Aucune ligne configurée. Créez-les dans Paramètres → Production.
                            </p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-1">
                            Shift
                            <span className="text-xs text-slate-400">(ouvriers)</span>
                          </Label>
                          <Select
                            value={formData.shift || 'none'}
                            onValueChange={(value) => handleSelectChange('shift', value === 'none' ? '' : value)}
                          >
                            <SelectTrigger className="h-11" data-testid="employee-shift-select">
                              <SelectValue placeholder="Sélectionner un shift" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">— Aucun —</SelectItem>
                              {shifts.map(s => (
                                <SelectItem key={s.id} value={s.id}>⏰ {s.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {shifts.length === 0 && (
                            <p className="text-xs text-amber-600">
                              Aucun shift configuré. Créez-les dans Paramètres → Production.
                            </p>
                          )}
                        </div>
                      </>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="dateNaissance">Date de naissance</Label>
                      <Input
                        id="dateNaissance"
                        name="dateNaissance"
                        type="date"
                        value={formData.dateNaissance}
                        onChange={handleChange}
                        className="h-11"
                      />
                    </div>
                  </div>

                  {/* Section Sortie */}
                  {formData.status === 'Sorti' && (
                    <div className="p-4 bg-red-50 rounded-xl border border-red-200 space-y-4">
                      <h4 className="font-medium text-red-900 flex items-center gap-2">
                        🚪 Informations de sortie
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="dateSortie">Date de sortie</Label>
                          <Input
                            id="dateSortie"
                            name="dateSortie"
                            type="date"
                            value={formData.dateSortie}
                            onChange={handleChange}
                            className="h-11 bg-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Motif de sortie</Label>
                          <Select
                            value={formData.motifSortie || "none"}
                            onValueChange={(value) => handleSelectChange('motifSortie', value === "none" ? "" : value)}
                          >
                            <SelectTrigger className="h-11 bg-white">
                              <SelectValue placeholder="Sélectionner le motif" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Non renseigné</SelectItem>
                              <SelectItem value="Démission">Démission</SelectItem>
                              <SelectItem value="Licenciement">Licenciement</SelectItem>
                              <SelectItem value="Fin de CDD">Fin de CDD</SelectItem>
                              <SelectItem value="Retraite">Retraite</SelectItem>
                              <SelectItem value="Décès">Décès</SelectItem>
                              <SelectItem value="Autre">Autre</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 4: Récapitulatif */}
              {currentStep === 3 && (
                <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                  {/* Écran de succès après création */}
                  {submitSuccess ? (
                    <div className="text-center py-8">
                      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-in zoom-in duration-300">
                        <Check className="w-10 h-10 text-green-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-green-700 mb-2">Employé créé avec succès !</h3>
                      <p className="text-slate-600 mb-6">
                        <strong>{formData.firstName} {formData.lastName}</strong> a été ajouté à votre équipe.
                      </p>
                      
                      <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 text-left max-w-md mx-auto">
                        <h4 className="font-semibold text-green-800 mb-2">Récapitulatif</h4>
                        <ul className="text-sm text-green-700 space-y-1">
                          <li>📧 Email : {formData.email}</li>
                          <li>💼 Poste : {formData.jobTitle}</li>
                          <li>🏢 Département : {getDepartmentName(formData.departmentId)}</li>
                          <li>📋 Contrat : {formData.typeContrat}</li>
                          {formData.matricule && <li>🔢 Matricule : {formData.matricule}</li>}
                        </ul>
                      </div>

                      {/* Affichage du compte créé */}
                      {generatedPassword && (
                        <div className="bg-blue-50 border border-blue-300 rounded-xl p-4 mb-6 text-left max-w-md mx-auto">
                          <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                            <Key className="w-4 h-4" />
                            Compte utilisateur créé
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center justify-between">
                              <span className="text-blue-700">Email :</span>
                              <span className="font-mono font-medium">{formData.email}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-blue-700">Mot de passe :</span>
                              <div className="flex items-center gap-2">
                                <span className="font-mono font-medium bg-white px-2 py-1 rounded border">
                                  {generatedPassword}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => {
                                    navigator.clipboard.writeText(generatedPassword);
                                    toast.success('Mot de passe copié !');
                                  }}
                                >
                                  <Copy className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                          <p className="text-xs text-blue-600 mt-3 p-2 bg-blue-100 rounded">
                            ⚠️ Conservez ce mot de passe ! Il ne sera plus affiché.
                            L'employé pourra le changer après sa première connexion.
                          </p>
                        </div>
                      )}
                      
                      <div className="flex gap-3 justify-center">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setSubmitSuccess(false);
                            setCreatedEmployee(null);
                            setGeneratedPassword('');
                            setCreateUserAccount(false);
                            setFormData({
                              firstName: '', lastName: '', email: '', phone: '', jobTitle: '',
                              photoUrl: '', departmentId: '', siteId: '', managerId: '',
                              status: 'Actif', hireDate: '', userId: '', jobProfileId: '',
                              matricule: '', sexe: 'Homme', situationFamiliale: 'Non renseigné',
                              typeContrat: 'CDI', dateNaissance: '', dateSortie: '',
                              categorieProfessionnelle: 'Non renseigné', motifSortie: '',
                              typeEntree: 'Nouvelle recrue'
                            });
                            setCurrentStep(0);
                          }}
                        >
                          <UserPlus className="w-4 h-4 mr-2" />
                          Créer un autre employé
                        </Button>
                        <Button
                          onClick={() => navigate('/employees')}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          Voir la liste des employés
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                  <>
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">Vérifiez les informations</h3>
                    <p className="text-slate-500">Confirmez que tout est correct avant de sauvegarder</p>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    {/* Identité */}
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                        <User className="w-4 h-4" /> Identité
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Nom complet</span>
                          <span className="font-medium">{formData.firstName} {formData.lastName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Email</span>
                          <span className="font-medium">{formData.email}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Téléphone</span>
                          <span className="font-medium">{formData.phone || '-'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Professionnel */}
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                        <Briefcase className="w-4 h-4" /> Professionnel
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Poste</span>
                          <span className="font-medium">{formData.jobTitle}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Département</span>
                          <span className="font-medium">{getDepartmentName(formData.departmentId)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Manager Principal</span>
                          <span className="font-medium">{getManagerName(formData.managerId)}</span>
                        </div>
                        {formData.siteManagerId && (
                          <div className="flex justify-between">
                            <span className="text-slate-500">Manager de Site</span>
                            <span className="font-medium text-blue-600">{getSiteManagerName(formData.siteManagerId)}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-slate-500">Statut</span>
                          <Badge variant={formData.status === 'Actif' ? 'default' : 'secondary'}>
                            {formData.status}
                          </Badge>
                        </div>
                        {formData.jobProfileId && (
                          <div className="flex justify-between pt-2 border-t border-slate-200 mt-2">
                            <span className="text-slate-500 flex items-center gap-1">
                              <Briefcase className="w-3 h-3 text-purple-500" /> Fiche de poste
                            </span>
                            <span className="font-medium text-purple-700">
                              {jobProfiles.find(p => p.id === formData.jobProfileId)?.title || jobProfiles.find(p => p.id === formData.jobProfileId)?.name || '-'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* RH */}
                    <div className="p-4 bg-slate-50 rounded-xl col-span-2">
                      <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                        <BarChart3 className="w-4 h-4" /> Informations RH
                      </h4>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500 block">Matricule</span>
                          <span className="font-medium">{formData.matricule || '-'}</span>
                        </div>
                        <div>
                          <span className="text-slate-500 block">Sexe</span>
                          <span className="font-medium">{formData.sexe}</span>
                        </div>
                        <div>
                          <span className="text-slate-500 block">Type contrat</span>
                          <span className="font-medium">{formData.typeContrat}</span>
                        </div>
                        <div>
                          <span className="text-slate-500 block">Catégorie</span>
                          <span className="font-medium">{formData.categorieProfessionnelle}</span>
                        </div>
                      </div>
                    </div>

                    {/* Option de création de compte utilisateur */}
                    {!isEditing && !formData.userId && (
                      <div className="p-4 bg-blue-50 rounded-xl col-span-2 border border-blue-200">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            id="createUserAccount"
                            checked={createUserAccount}
                            onCheckedChange={setCreateUserAccount}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <Label 
                              htmlFor="createUserAccount" 
                              className="font-medium text-blue-900 cursor-pointer flex items-center gap-2"
                            >
                              <Key className="w-4 h-4" />
                              Créer un compte utilisateur pour cet employé
                            </Label>
                            <p className="text-sm text-blue-700 mt-1">
                              Un compte sera créé avec l'email <strong>{formData.email || '(email requis)'}</strong>.
                              {!formData.email && (
                                <span className="text-red-600 ml-1">⚠️ Veuillez renseigner un email à l'étape 1.</span>
                              )}
                            </p>
                            {createUserAccount && formData.email && (
                              <div className="mt-2 text-sm text-blue-600 bg-blue-100 p-2 rounded">
                                <Mail className="w-4 h-4 inline mr-1" />
                                Un mot de passe temporaire sera généré et affiché après la création.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  </>
                  )}
                </div>
              )}

              {/* Navigation Buttons - Caché après succès */}
              {!submitSuccess && (
              <div className="flex justify-between mt-8 pt-6 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevStep}
                  disabled={currentStep === 0}
                  className="gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Précédent
                </Button>

                {currentStep < STEPS.length - 1 ? (
                  <Button
                    type="button"
                    onClick={nextStep}
                    className="gap-2 bg-blue-600 hover:bg-blue-700"
                  >
                    Suivant
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    disabled={saving || isSubmitting}
                    className="gap-2 bg-green-600 hover:bg-green-700"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    {isEditing ? 'Mettre à jour' : 'Créer l\'employé'}
                  </Button>
                )}
              </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Image Cropper Dialog */}
      <Dialog open={cropperOpen} onOpenChange={(open) => !open && handleCropCancel()}>
        <DialogContent className="max-w-3xl h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crop className="w-5 h-5" />
              Recadrer la photo
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 relative bg-slate-900 rounded-lg overflow-hidden min-h-[300px]">
            {imageToCrop && (
              <Cropper
                image={imageToCrop}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={1}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                cropShape="round"
                showGrid={false}
              />
            )}
          </div>

          {/* Controls */}
          <div className="space-y-4 pt-4">
            {/* Zoom control */}
            <div className="flex items-center gap-4">
              <ZoomOut className="w-4 h-4 text-slate-500" />
              <Slider
                value={[zoom]}
                onValueChange={(value) => setZoom(value[0])}
                min={1}
                max={3}
                step={0.1}
                className="flex-1"
              />
              <ZoomIn className="w-4 h-4 text-slate-500" />
              <span className="text-sm text-slate-500 w-12">{Math.round(zoom * 100)}%</span>
            </div>

            {/* Rotation control */}
            <div className="flex items-center gap-4">
              <RotateCcw className="w-4 h-4 text-slate-500" />
              <Slider
                value={[rotation]}
                onValueChange={(value) => setRotation(value[0])}
                min={-180}
                max={180}
                step={1}
                className="flex-1"
              />
              <span className="text-sm text-slate-500 w-12">{rotation}°</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setRotation(0)}
              >
                Reset
              </Button>
            </div>

            {/* Info */}
            <p className="text-xs text-slate-500 text-center">
              💡 L'image sera automatiquement compressée après le recadrage pour optimiser la taille du fichier.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCropCancel}
              disabled={uploading}
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={handleCropConfirm}
              disabled={uploading}
              className="bg-webtech-blue hover:bg-webtech-blue-hover"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Traitement...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Appliquer et uploader
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog complet pour créer une fiche de poste */}
      <JobProfileDialog
        open={showCreateJobProfileDialog}
        onOpenChange={setShowCreateJobProfileDialog}
        mode="create"
        companyId={user?.companyId}
        departments={departments}
        prefilledName={formData.jobTitle} // Pré-remplir avec le poste de l'employé
        prefilledDepartmentId={formData.departmentId}
        onSaveSuccess={handleJobProfileCreated}
      />

      {/* Dialog : proposer la synchro des évaluations après changement de manager */}
      <AlertDialog open={managerSyncDialog.open} onOpenChange={(open) => {
        if (!open && !managerSyncDialog.syncing) handleSkipSync();
      }}>
        <AlertDialogContent data-testid="manager-sync-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Mettre à jour les évaluations en cours ?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-600">
                <p>
                  Vous venez de changer le manager de {formData.firstName} {formData.lastName}.
                </p>
                <p>
                  <span className="font-semibold text-slate-900">
                    {managerSyncDialog.count} évaluation{managerSyncDialog.count > 1 ? 's' : ''} en cours
                  </span>{' '}
                  {managerSyncDialog.count > 1 ? 'sont' : 'est'} encore attribuée{managerSyncDialog.count > 1 ? 's' : ''} à l'ancien manager.
                </p>
                <p className="text-xs text-emerald-700 bg-emerald-50 rounded px-2 py-1.5 border border-emerald-200">
                  ℹ Les notes et commentaires déjà saisis seront conservés — seul le destinataire change.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={handleSkipSync}
              disabled={managerSyncDialog.syncing}
              data-testid="manager-sync-skip"
            >
              Non, garder l'ancien manager
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSyncEvaluations}
              disabled={managerSyncDialog.syncing}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="manager-sync-confirm"
            >
              {managerSyncDialog.syncing
                ? 'Synchronisation...'
                : `Oui, mettre à jour (${managerSyncDialog.count})`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}


// Wrapper avec ErrorBoundary pour éviter les pages blanches
export default function EmployeeFormPage() {
  return (
    <ErrorBoundary>
      <EmployeeFormPageContent />
    </ErrorBoundary>
  );
}
